import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import {
  CompositeEmailMetadataFilter,
  EmailFilter,
  EmailFilterOperator,
  EmailFilterPayload,
  EmailMetadataFilter,
  EmailMetadataFilterAttribute,
  IndividualEmailMetadataFilter,
  IndividualEmailMetadataFilterValue,
} from '../../models/EmailFilter'

export interface EmailFilterTag {
  attr: EmailMetadataFilterAttribute
  val: string | boolean
}

interface EmailFiltersState {
  isLoading: boolean
  emailFilters: EntityState<EmailFilter, string>
  selectedQuickFilter: EmailFilter | null
  editingQuickFilter: EmailFilter | null
  sender: string | null
  before: string | null
  after: string | null
  emailTypeIds: string[]
  tags: EmailFilterTag[]
  showFilterModal: boolean
  filterModalMode: 'view' | 'edit' | 'new'
}

const emailFiltersAdapter = createEntityAdapter<EmailFilter>()

const initialState: EmailFiltersState = {
  isLoading: true,
  emailFilters: emailFiltersAdapter.getInitialState(),
  selectedQuickFilter: null,
  editingQuickFilter: null,
  sender: null,
  before: null,
  after: null,
  emailTypeIds: [],
  tags: [],
  showFilterModal: false,
  filterModalMode: 'view',
}

export const emailFilterSlice = createSlice({
  name: 'emailFilters',
  initialState,
  reducers: {
    setEmailFiltersLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setShowEmailFilterModal: (state, action: PayloadAction<boolean>) => {
      const show = action.payload
      state.showFilterModal = show
      if (!show) {
        state.editingQuickFilter = null
      }
    },
    setEmailFilterModalMode: (state, action: PayloadAction<'view' | 'edit' | 'new'>) => {
      state.filterModalMode = action.payload
    },
    setEmailFilters: (state, action: PayloadAction<EmailFilter[]>) => {
      emailFiltersAdapter.setAll(state.emailFilters, action.payload)
    },
    setEmailFilter: (state, action: PayloadAction<EmailFilter>) => {
      emailFiltersAdapter.setOne(state.emailFilters, action.payload)
    },
    removeEmailFilter: (state, action: PayloadAction<string>) => {
      emailFiltersAdapter.removeOne(state.emailFilters, action.payload)
    },
    setSelectedQuickEmailFilter: (state, action: PayloadAction<EmailFilter>) => {
      const quickFilter = action.payload
      const {
        filter: { senders, before, after, filter: metadataFilter },
      } = quickFilter
      state.selectedQuickFilter = quickFilter
      state.sender = senders?.length ? senders[0] : null
      state.before = before ?? null
      state.after = after ?? null
      if (metadataFilter) {
        const individualFilterValues = _getIndividualFilterValues(metadataFilter)
        const emailTypeIdFilters = individualFilterValues.filter((f) => f.attr === 'emailType').map((f) => f.val as string)
        state.emailTypeIds = emailTypeIdFilters.length ? emailTypeIdFilters : []
        state.tags = individualFilterValues
          .filter((f) => f.attr === 'new' || f.attr === 'processed' || f.attr === 'replyGenerated')
          .map((f) => ({ attr: f.attr, val: f.val }))
      }
    },
    setEditingQuickEmailFilter: (state, action: PayloadAction<EmailFilter>) => {
      state.editingQuickFilter = action.payload
    },
    setEmailFilterSender: (state, action: PayloadAction<string | null>) => {
      state.selectedQuickFilter = null
      state.sender = action.payload
    },
    setEmailFilterBefore: (state, action: PayloadAction<string | null>) => {
      state.selectedQuickFilter = null
      state.before = action.payload
    },
    setEmailFilterAfter: (state, action: PayloadAction<string | null>) => {
      state.selectedQuickFilter = null
      state.after = action.payload
    },
    setEmailFilterEmailTypeIds: (state, action: PayloadAction<string[]>) => {
      state.selectedQuickFilter = null
      state.emailTypeIds = action.payload
    },
    setEmailFilterTags: (state, action: PayloadAction<EmailFilterTag[]>) => {
      state.selectedQuickFilter = null
      state.tags = action.payload
    },
    resetCustomEmailFilter: (state, action: PayloadAction<{ withoutContactFilter?: boolean } | undefined>) => {
      const { withoutContactFilter = false } = action.payload ?? {}
      if (!withoutContactFilter) {
        state.sender = null
      }
      state.before = today()
      state.after = twoWeeksAgo()
      state.emailTypeIds = []
      state.tags = []
    },
    resetEmailFilters: () => initialState,
  },
})

export const {
  setEmailFiltersLoading,
  setEmailFilters,
  setEmailFilter,
  removeEmailFilter,
  setShowEmailFilterModal,
  setEmailFilterModalMode,
  setSelectedQuickEmailFilter,
  setEditingQuickEmailFilter,
  setEmailFilterSender,
  setEmailFilterBefore,
  setEmailFilterAfter,
  setEmailFilterEmailTypeIds,
  setEmailFilterTags,
  resetCustomEmailFilter,
  resetEmailFilters,
} = emailFilterSlice.actions

const _getIndividualFilterValues = (filter: EmailMetadataFilter): IndividualEmailMetadataFilterValue[] => {
  if (filter.type === 'individual') {
    return [filter.filter]
  } else if (filter.type === 'composite') {
    return filter.filters.reduce((acc: IndividualEmailMetadataFilterValue[], f) => {
      return acc.concat(_getIndividualFilterValues(f))
    }, [])
  }
  return []
}

const emailFilterSelectors = emailFiltersAdapter.getSelectors((state: RootState) => state.emailHub.emailFilters.emailFilters)

export const selectEmailFilters = (state: RootState) => emailFilterSelectors.selectAll(state)

export const selectSelectedQuickEmailFilter = (state: RootState) => state.emailHub.emailFilters.selectedQuickFilter

export const selectEditingQuickEmailFilter = (state: RootState) => state.emailHub.emailFilters.editingQuickFilter

export const selectDefaultEmailFilter = (state: RootState) => {
  const emailFilters = emailFilterSelectors.selectAll(state)
  if (!emailFilters.length) {
    return null
  }
  return emailFilters.find((filter) => filter.isDefault) ?? emailFilters[0]
}

export const selectEmailFilterSender = (state: RootState) => state.emailHub.emailFilters.sender

export const selectEmailFilterBefore = (state: RootState) => state.emailHub.emailFilters.before

export const selectEmailFilterAfter = (state: RootState) => state.emailHub.emailFilters.after

export const selectEmailFilterEmailTypeIds = (state: RootState) => state.emailHub.emailFilters.emailTypeIds

export const selectEmailFilterTags = (state: RootState) => state.emailHub.emailFilters.tags

export const selectShowEmailFilterModal = (state: RootState) => state.emailHub.emailFilters.showFilterModal

export const selectEmailFilterModalMode = (state: RootState) => state.emailHub.emailFilters.filterModalMode

export const selectCustomEmailFilter = createSelector(
  [selectEmailFilterSender, selectEmailFilterBefore, selectEmailFilterAfter, selectEmailFilterEmailTypeIds, selectEmailFilterTags],
  (sender, before, after, emailTypeIds, tags): EmailFilterPayload => {
    const emailTypeFilter: IndividualEmailMetadataFilter[] = emailTypeIds.length
      ? [{ type: 'individual', filter: { attr: 'emailType', val: emailTypeIds.join(), op: EmailFilterOperator.IN } }]
      : []

    const tagFilter: CompositeEmailMetadataFilter[] = tags.length
      ? [
          {
            type: 'composite',
            filters: tags.map(({ attr, val }) => ({
              type: 'individual',
              filter: { attr, val, op: EmailFilterOperator.EQUALS },
            })),
            operator: 'or',
          },
        ]
      : []
    const metadataFilters = [...emailTypeFilter, ...tagFilter]
    return {
      senders: sender ? [sender] : [],
      ...(before && { before }),
      ...(after && { after }),
      ...(metadataFilters.length && {
        filter: {
          type: 'composite',
          filters: metadataFilters,
          operator: 'and',
        },
      }),
    }
  },
)

export const selectEmailFilterCount = createSelector(
  [selectEmailFilterSender, selectEmailFilterBefore, selectEmailFilterAfter, selectEmailFilterEmailTypeIds, selectEmailFilterTags],
  (sender, before, after, emailTypeIds, tags) => {
    return (sender ? 1 : 0) + (before || after ? 1 : 0) + emailTypeIds.length + tags.length
  },
)

const today = () => {
  const date = new Date()
  date.setHours(23, 59, 59, 999)
  return date.toISOString()
}

const twoWeeksAgo = () => {
  const date = new Date()
  date.setDate(date.getDate() - 14)
  return date.toISOString()
}
